import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Base from "../../Base/views/Base";
import Box from '@mui/material/Box';
import {
    Button,
    Card,
    Grid,
    Typography,
    TextField,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    Chip,
    IconButton,
    Divider,
    Stack
} from "@mui/material";
import Popup from "../../components/Popup";
import Swal from "sweetalert2";
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import axiosInstance from "../../../axiosInstance";

const ExpenseLandingPage = () => {
    const [hotelID] = useState(localStorage.getItem("billing_app_hotel"));
    const [data, setData] = useState([]);
    const [categories, setCategories] = useState([]);
    const [open, setOpen] = useState(false);

    const initialValues = {
        category: "",
        amount: "",
        description: "",
        expense_date: new Date().toISOString().split('T')[0],
        payment_mode: "Cash",
        vendor_name: "",
        invoice_number: "",
        hotel: ""
    };

    const [inputData, setInputData] = useState(initialValues);
    const [errors, setErrors] = useState({});

    useEffect(() => {
        getData();
        getCategories();
    }, []);

    const getData = () => {
        axiosInstance(`/api/expense/?hotel=${hotelID}`, {
            method: "GET"
        }).then(res => {
            setData(res.data);
        }).catch(err => {
            console.log(err);
        });
    };

    const getCategories = () => {
        axiosInstance(`/api/expense-category/?hotel=${hotelID}`, {
            method: "GET"
        }).then(res => {
            setCategories(res.data);
        }).catch(err => {
            console.error(err);
        });
    };

    const validate = () => {
        let temp = { ...errors };
        if (!inputData.category) temp.category = "Category is required";
        if (!inputData.amount || inputData.amount <= 0) temp.amount = "Valid amount is required";
        if (!inputData.expense_date) temp.expense_date = "Date is required";
        setErrors(temp);
        return Object.values(temp).every(x => x === "");
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setInputData({ ...inputData, [name]: value });
        if (errors[name]) {
            setErrors({ ...errors, [name]: "" });
        }
    };

    const handleClose = () => {
        setOpen(false);
        setInputData(initialValues);
        setErrors({});
    };

    const handleSubmit = () => {
        if (validate()) {
            const api = inputData?.id ? `/api/expense/${inputData.id}/` : `/api/expense/`;
            const method = inputData?.id ? "PUT" : "POST";

            axiosInstance(api, {
                method: method,
                data: {
                    category: inputData.category,
                    amount: inputData.amount,
                    description: inputData.description,
                    expense_date: inputData.expense_date,
                    payment_mode: inputData.payment_mode,
                    vendor_name: inputData.vendor_name,
                    invoice_number: inputData.invoice_number,
                    hotel: hotelID
                }
            }).then(res => {
                getData();
                setOpen(false);
                setInputData(initialValues);
                Swal.fire({
                    icon: 'success',
                    title: inputData?.id ? 'Expense updated!' : 'Expense added!',
                    timer: 1500
                });
            }).catch(err => {
                let errorMessage = 'Failed to save expense';
                if (err?.response?.data) {
                    if (typeof err.response.data === 'string') {
                        errorMessage = err.response.data;
                    } else if (err.response.data.detail) {
                        errorMessage = err.response.data.detail;
                    } else {
                        errorMessage = JSON.stringify(err.response.data);
                    }
                }
                Swal.fire({
                    icon: 'error',
                    title: 'Error',
                    text: errorMessage
                });
            });
        }
    };

    const handleClick = (item) => {
        setInputData({
            ...item,
            expense_date: item.expense_date ? item.expense_date.split('T')[0] : new Date().toISOString().split('T')[0]
        });
        setOpen(true);
    };

    const handleDelete = (e, id) => {
        e?.stopPropagation();
        Swal.fire({
            title: "Are you sure?",
            text: "This expense will be deleted.",
            showCancelButton: true,
            confirmButtonText: "Delete",
            icon: 'warning',
            reverseButtons: true,
        }).then((result) => {
            if (result.isConfirmed) {
                axiosInstance(`/api/expense/${id}/`, {
                    method: "DELETE"
                }).then(res => {
                    getData();
                    Swal.fire({
                        icon: 'success',
                        title: 'Deleted!',
                        timer: 1500
                    });
                }).catch(err => {
                    Swal.fire({
                        icon: 'error',
                        title: 'Error',
                        text: 'Failed to delete expense'
                    });
                });
            }
        });
    };

    const totalExpenses = data.reduce((sum, item) => sum + parseFloat(item.amount || 0), 0);

    return (
        <Base>
            <Popup
                open={open}
                handleClose={handleClose}
                title={inputData?.id ? "Edit Expense" : "Add Expense"}
                handleSubmit={handleSubmit}
                save={true}
                maxWidth="md"
            >
                <Box sx={{ mt: 2 }}>
                    <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                        <InputLabel>Category</InputLabel>
                        <Select
                            name="category"
                            value={inputData.category || ""}
                            onChange={handleChange}
                            label="Category"
                            error={!!errors.category}
                        >
                            {categories.map((cat) => (
                                <MenuItem key={cat.id} value={cat.id}>
                                    {cat.name}
                                </MenuItem>
                            ))}
                        </Select>
                        {errors.category && (
                            <Typography variant="caption" color="error" sx={{ mt: 0.5 }}>
                                {errors.category}
                            </Typography>
                        )}
                    </FormControl>

                    <TextField
                        label="Amount"
                        type="number"
                        fullWidth
                        name="amount"
                        value={inputData.amount || ""}
                        onChange={handleChange}
                        size="small"
                        sx={{ mb: 2 }}
                        error={!!errors.amount}
                        helperText={errors.amount}
                        InputProps={{
                            startAdornment: <span style={{ marginRight: 8 }}>₹</span>
                        }}
                    />

                    <TextField
                        label="Expense Date"
                        type="date"
                        fullWidth
                        name="expense_date"
                        value={inputData.expense_date || ""}
                        onChange={handleChange}
                        size="small"
                        sx={{ mb: 2 }}
                        InputLabelProps={{ shrink: true }}
                        error={!!errors.expense_date}
                        helperText={errors.expense_date}
                    />

                    <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                        <InputLabel>Payment Mode</InputLabel>
                        <Select
                            name="payment_mode"
                            value={inputData.payment_mode || "Cash"}
                            onChange={handleChange}
                            label="Payment Mode"
                        >
                            <MenuItem value="Cash">Cash</MenuItem>
                            <MenuItem value="Online">Online</MenuItem>
                            <MenuItem value="Cheque">Cheque</MenuItem>
                        </Select>
                    </FormControl>

                    <TextField
                        label="Vendor Name"
                        fullWidth
                        name="vendor_name"
                        value={inputData.vendor_name || ""}
                        onChange={handleChange}
                        size="small"
                        sx={{ mb: 2 }}
                    />

                    <TextField
                        label="Invoice Number"
                        fullWidth
                        name="invoice_number"
                        value={inputData.invoice_number || ""}
                        onChange={handleChange}
                        size="small"
                        sx={{ mb: 2 }}
                    />

                    <TextField
                        label="Description"
                        fullWidth
                        multiline
                        rows={3}
                        name="description"
                        value={inputData.description || ""}
                        onChange={handleChange}
                        size="small"
                        sx={{ mb: 2 }}
                    />
                </Box>
            </Popup>

            <Box sx={{ display: "flex", justifyContent: "flex-end", alignItems: "center", mb: 2, gap: 1.5, flexWrap: 'wrap' }}>
                <Card 
                    sx={{ 
                        p: 1.5, 
                        minWidth: 140, 
                        textAlign: "center",
                        background: 'linear-gradient(135deg, #fff5f5 0%, #ffe8e8 100%)',
                        border: '1px solid rgba(255, 72, 66, 0.1)',
                    }}
                >
                    <Typography variant="caption" color="text.secondary" sx={{ mb: 0.25, fontWeight: 500, fontSize: '0.75rem' }}>
                        Total Expenses
                    </Typography>
                    <Typography variant="h6" color="error" sx={{ fontWeight: 700, fontSize: '1.1rem' }}>
                        ₹{totalExpenses.toFixed(2)}
                    </Typography>
                </Card>
                <Button 
                    variant="contained" 
                    onClick={() => setOpen(true)}
                    size="small"
                    sx={{ 
                        borderRadius: 1,
                        px: 1.5,
                        py: 0.5,
                        minHeight: 0,
                        fontSize: '0.8rem',
                        textTransform: 'none',
                        fontWeight: 600,
                        boxShadow: '0 2px 6px rgba(139, 68, 255, 0.25)',
                        '&:hover': {
                            boxShadow: '0 2px 10px rgba(139, 68, 255, 0.35)',
                            transform: 'translateY(-1px)',
                        },
                        transition: 'all 0.3s ease',
                    }}
                >
                    Add Expense
                </Button>
                <Button 
                    component={Link}
                    to="/expense-category"
                    variant="outlined"
                    color="primary"
                    size="small"
                    sx={{ 
                        borderRadius: 1,
                        px: 1.5,
                        py: 0.5,
                        minHeight: 0,
                        fontSize: '0.8rem',
                        textTransform: 'none',
                        fontWeight: 600,
                        borderColor: 'primary.main',
                        color: 'primary.main',
                        '&:hover': {
                            borderColor: 'primary.dark',
                            color: 'primary.dark',
                            backgroundColor: 'action.hover',
                        },
                        transition: 'all 0.3s ease',
                    }}
                >
                    Expense Categories
                </Button>
            </Box>

            <Box sx={{ flexGrow: 1 }}>
                <Grid container spacing={2}>
                    {data.length === 0 ? (
                        <Grid item xs={12}>
                            <Box 
                                sx={{ 
                                    mt: 8, 
                                    textAlign: "center",
                                    py: 8,
                                }}
                            >
                                <Typography 
                                    variant="h5" 
                                    sx={{ 
                                        color: 'text.secondary',
                                        mb: 2,
                                        fontWeight: 500,
                                    }}
                                >
                                    No expenses yet
                                </Typography>
                                <Typography 
                                    variant="body1" 
                                    sx={{ 
                                        color: 'text.secondary',
                                        mb: 4,
                                    }}
                                >
                                    Start tracking your expenses to manage your finances better
                                </Typography>
                                <Button 
                                    variant="contained" 
                                    onClick={() => setOpen(true)}
                                    sx={{
                                        borderRadius: 2,
                                        px: 4,
                                        py: 1.5,
                                        textTransform: 'none',
                                        fontWeight: 600,
                                    }}
                                >
                                    Add Your First Expense
                                </Button>
                            </Box>
                        </Grid>
                    ) : (
                        data.map((item) => (
                            <Grid item xs={12} sm={6} md={4} key={item.id}>
                                <Card
                                    variant="outlined"
                                    sx={{
                                        width: "100%",
                                        transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                                        borderRadius: 2,
                                        boxShadow: '0 1px 4px rgba(0, 0, 0, 0.08)',
                                        "&:hover": {
                                            boxShadow: '0 4px 12px rgba(139, 68, 255, 0.15)',
                                            transform: "translateY(-3px)",
                                            borderColor: 'primary.main',
                                        },
                                        cursor: "pointer",
                                        p: 1.5,
                                        border: '1px solid rgba(0, 0, 0, 0.08)',
                                    }}
                                    onClick={() => handleClick(item)}
                                >
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
                                        <Box>
                                            <Chip label={item.category_name || item.category} size="small" sx={{ mb: 1 }} />
                                            <Typography variant="h6" sx={{ fontWeight: "bold", color: "error" }}>
                                                ₹{parseFloat(item.amount).toFixed(2)}
                                            </Typography>
                                        </Box>
                                        <Box>
                                            <IconButton size="small" onClick={(e) => {
                                                e.stopPropagation();
                                                handleClick(item);
                                            }}>
                                                <EditIcon fontSize="small" />
                                            </IconButton>
                                            <IconButton size="small" onClick={(e) => {
                                                e.stopPropagation();
                                                handleDelete(e, item.id);
                                            }}>
                                                <DeleteIcon fontSize="small" color="error" />
                                            </IconButton>
                                        </Box>
                                    </Box>
                                    <Divider sx={{ my: 1 }} />
                                    <Stack spacing={1}>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                            <Typography variant="body2" color="text.secondary">Date:</Typography>
                                            <Typography>{new Date(item.expense_date).toLocaleDateString()}</Typography>
                                        </Box>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                            <Typography variant="body2" color="text.secondary">Payment:</Typography>
                                            <Typography>{item.payment_mode}</Typography>
                                        </Box>
                                        {item.vendor_name && (
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                                <Typography variant="body2" color="text.secondary">Vendor:</Typography>
                                                <Typography>{item.vendor_name}</Typography>
                                            </Box>
                                        )}
                                        {item.invoice_number && (
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                                <Typography variant="body2" color="text.secondary">Invoice:</Typography>
                                                <Typography>{item.invoice_number}</Typography>
                                            </Box>
                                        )}
                                        {item.description && (
                                            <Box>
                                                <Typography variant="body2" color="text.secondary">Description:</Typography>
                                                <Typography variant="body2">{item.description}</Typography>
                                            </Box>
                                        )}
                                    </Stack>
                                </Card>
                            </Grid>
                        ))
                    )}
                </Grid>
            </Box>
        </Base>
    );
};

export default ExpenseLandingPage;
